import { useNavigate, useSearchParams } from "react-router-dom";
import { useTenantStore } from "@/stores/tenantStore";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@repo/ui/components/Base/Button";
import { SelectSubscriptionStep } from "@/components/setupSteps/SelectSubscription";
import { CheckOutStep } from "@/components/setupSteps/CheckoutStep";
import { VerifySubscriptionStep } from "@/components/setupSteps/VerifySubscriptionStep";
import { SelectMunicipalityStep } from "@/components/setupSteps/SelectMunicipalityStep";
import { Formik } from "formik";
import { tenantRepo } from "@/repositories/tenantRepo";
import { firstValueFrom, take } from "rxjs";
import { userRepo } from "@/repositories/userRepo";

const CompleteSubscription = () => {
  const navigate = useNavigate();

  useEffect(() => {
    navigate("/app");
  }, [navigate]);
  return null;
};
export const NewSubscriptionPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const stepId = useMemo(
    () => searchParams.get("stepId") ?? "0",
    [searchParams]
  );

  const { setTenant, tenant: tenantData } = useTenantStore();

  const [adminUsers, setAdminUsers] = useState<string[]>([]);
  useEffect(() => {
    if (tenantData?.adminUsers) {
      Promise.all(
        tenantData.adminUsers.map((userId) =>
          firstValueFrom(userRepo.get(userId))
        )
      ).then((users) =>
        setAdminUsers(users.filter((user) => !!user).map((user) => user!.email))
      );
    }
  }, [tenantData]);

  useEffect(() => {
    const tenantId = searchParams.get("tenantId");
    if (tenantId) {
      tenantRepo
        .get(tenantId)
        .pipe(take(1))
        .subscribe((tenant) => setTenant(tenant));
    }
  }, [searchParams, setTenant]);

  const handleNextStep = () => {
    setSearchParams((searchParams) => {
      searchParams.set("tenantId", tenantData?.id ?? "");
      searchParams.set(
        "stepId",
        `${parseInt(searchParams.get("stepId") ?? "0") + 1}`
      );
      return searchParams;
    });
  };

  const STEPS = [
    // stepId = 1:
    {
      tenantIdRequired: false,
      component: (key: string) => {
        return (
          <div key={key} className="flex flex-col gap-4">
            <h1>
              Your subscription has either expired or has not been activated.
              Please subscribe to a plan in order to continue using this
              product.
            </h1>
            <div>
              <p className="mb-1">
                If you are not able to authorize the subscription. Contact one
                of your admin users:
              </p>
              <ul>
                {adminUsers.map((user, index) => (
                  <li key={index}>
                    <em>{user}</em>
                  </li>
                ))}
              </ul>
            </div>
            <Button onClick={handleNextStep}>Continue</Button>
          </div>
        );
      },
    },
    {
      tenantIdRequired: true,
      component: (key: string) => {
        if (!tenantData?.id) return null;

        return (
          <div key={key} className="flex flex-col gap-4">
            <Formik
              key={key}
              initialValues={{ ...tenantData }}
              onSubmit={async (values) => {
                const updated = await tenantRepo.update(tenantData.id, {
                  ...values,
                });
                setTenant(updated);
                setSearchParams({ stepId: "3", tenantId: updated.id });
              }}
            >
              {({ handleSubmit, isSubmitting }) => (
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <SelectMunicipalityStep isSubmitting={isSubmitting} />
                </form>
              )}
            </Formik>
          </div>
        );
      },
    },
    // stepId = 2:
    {
      tenantIdRequired: true,
      component: (key: string) => {
        if (!tenantData?.id) {
          return null;
        }

        return (
          <div key={key} className="flex flex-col gap-4">
            <SelectSubscriptionStep
              organizationMunicipalityId={
                tenantData?.organizationMunicipalityId
              }
              organizationType={tenantData?.organizationMunicipalityType}
              tenantId={tenantData?.id}
            />
          </div>
        );
      },
    },
    // stepId = 3:
    {
      tenantIdRequired: true,
      component: (key: string) => {
        return (
          <div key={key} className="flex flex-col gap-4">
            <CheckOutStep />
          </div>
        );
      },
    },
    // stepId = 4:
    {
      tenantIdRequired: true,
      component: (key: string) => {
        return (
          <div key={key} className="flex flex-col gap-4">
            <VerifySubscriptionStep />
          </div>
        );
      },
    },
    // stepId = 5:
    {
      tenantIdRequired: false,
      component: (key: string) => {
        return (
          <div key={key} className="flex flex-col gap-4">
            <CompleteSubscription />
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col flex-grow">
      <div className="mx-auto mt-12 max-w-screen-lg w-full bg-form p-4 rounded-lg ">
        {STEPS.map((step, index) => {
          if (index + 1 === parseInt(stepId)) {
            if (step.tenantIdRequired) {
              if (tenantData) {
                return step.component(`step-${index}`);
              }
              return null;
            }

            return step.component(`step-${index}`);
          }
          return null;
        })}
      </div>
    </div>
  );
};
